// App-access authorization probe + sign-out helper.
//
// Authentication (does the user hold a valid token) is owned by Cognito/Amplify.
// AUTHORIZATION — is that authenticated user provisioned in the Control Center
// allowlist — is answered only by the backend. checkAppAccess() asks GET
// /auth/me and maps the HTTP status to a decision the Authorization gate acts on:
//   200 → allowed        401 → unauthenticated (re-login)
//   403 → forbidden      other/network → error (fail closed, never render app)

import { apiService } from "src/services";
import { IEnvelope, unwrap } from "src/utils/kcc";
import { isCognitoLoginAllowed } from "src/config/loginMethods";
import { environment } from "src/config/environment";
import { clearCognitoToken } from "./cognitoAuth";
import { goToLoginPicker, markCognitoSignedOut, suppressCognitoAutoLogin } from "./cognitoAutoLogin";

export type AppAccess = "allowed" | "forbidden" | "unauthenticated" | "error";

// When access is "error", WHY we couldn't verify — so the screen doesn't frame an
// outage as an access problem:
//   "server"  → the server/gateway answered with an error (5xx, 502 Bad Gateway,
//               503) — the service is down/degraded, nothing to do with the user.
//   "network" → we never reached it at all (offline, DNS/TLS, CORS, timeout).
export type AccessErrorReason = "server" | "network";

export interface AppAccessResult {
	access: AppAccess;
	email: string | null;
	reason?: AccessErrorReason;
}

interface IMe {
	user_email?: string;
}

const statusOf = (err: unknown): number | undefined =>
	(err as { response?: { status?: number } })?.response?.status;

// Map a failed probe to a decision. 403 → forbidden, 401 → re-login, anything
// else → fail closed to the retry screen (never the app), tagged server vs network.
const mapAuthError = (err: unknown): AppAccessResult => {
	const status = statusOf(err);
	if (status === 403) return { access: "forbidden", email: null };
	if (status === 401) return { access: "unauthenticated", email: null };
	// A status code means the server/gateway replied with an error (e.g. 502);
	// no status means the request never got a response (network/CORS/timeout).
	return { access: "error", email: null, reason: status ? "server" : "network" };
};

export const checkAppAccess = async (): Promise<AppAccessResult> => {
	try {
		const res = await apiService.get<IEnvelope<IMe>>("/auth/me");
		const me = unwrap<IMe>(res);
		return { access: "allowed", email: me?.user_email ?? null };
	} catch (err) {
		// Rollout skew: the FE may be deployed ahead of the BE, so /auth/me can be
		// absent (404). Fall back to an existing superadmin-gated endpoint that has
		// the same authz semantics (200 allowed / 403 forbidden / 401 unauth) so the
		// gate still works. Once /auth/me is live this branch never runs.
		if (statusOf(err) === 404) {
			try {
				await apiService.get("/tenants/stats");
				return { access: "allowed", email: null };
			} catch (fallbackErr) {
				return mapAuthError(fallbackErr);
			}
		}
		return mapAuthError(err);
	}
};

// Send an unauthenticated user back to sign in. Cognito-only for CTPL today;
// other IdPs fall back to the configured redirect.
export const routeToLogin = (): void => {
	if (isCognitoLoginAllowed) {
		goToLoginPicker();
		return;
	}
	if (environment.REACT_APP_REDIRECT_URL) window.location.href = environment.REACT_APP_REDIRECT_URL;
};

// Explicit sign-out from the access-denied screen (and the app avatar). For
// Cognito this arms the auto-login guard and tears down the Hosted-UI session so
// the next entry lands on the picker rather than silently re-authenticating.
export const signOutFromApp = async (): Promise<void> => {
	if (isCognitoLoginAllowed) {
		suppressCognitoAutoLogin();
		markCognitoSignedOut();
		// clearCognitoToken() redirects through Cognito's /logout back to /login;
		// do NOT add a manual redirect here (it would clobber that navigation).
		await clearCognitoToken();
		return;
	}
	window.location.href = environment.REACT_APP_SIGNOUT_URL || environment.REACT_APP_REDIRECT_URL || "";
};
